import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Edit2, Save, Tag } from 'lucide-react';
import { DiscountRule } from '../types';
import { describeDiscountRule } from '../utils/discount';
import { getStoredToken } from '../utils/authApi';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

interface OutletOption {
  id: number;
  name: string;
}

interface DiscountRulesModalProps {
  rules: DiscountRule[];
  onAddRule: (rule: Omit<DiscountRule, 'id' | 'createdAt'>) => void;
  onUpdateRule: (rule: DiscountRule) => void;
  onDeleteRule: (id: string) => void;
  onClose: () => void;
}

export default function DiscountRulesModal({
  rules,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
  onClose
}: DiscountRulesModalProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [outlets, setOutlets] = useState<OutletOption[]>([]);

  useEffect(() => {
    const token = getStoredToken();
    if (!token) return;
    fetch(`${BACKEND}/outlets`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setOutlets(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  // Add-row form state
  const [label, setLabel] = useState('');
  const [metric, setMetric] = useState<'visits' | 'spend'>('visits');
  const [threshold, setThreshold] = useState<number>(0);
  const [type, setType] = useState<'percent' | 'fixed'>('percent');
  const [value, setValue] = useState<number>(0);
  const [visibleOnBill, setVisibleOnBill] = useState(true);
  const [outletId, setOutletId] = useState<number | null>(null);

  // Edit-row form state
  const [editLabel, setEditLabel] = useState('');
  const [editMetric, setEditMetric] = useState<'visits' | 'spend'>('visits');
  const [editThreshold, setEditThreshold] = useState<number>(0);
  const [editType, setEditType] = useState<'percent' | 'fixed'>('percent');
  const [editValue, setEditValue] = useState<number>(0);
  const [editVisibleOnBill, setEditVisibleOnBill] = useState(true);
  const [editOutletId, setEditOutletId] = useState<number | null>(null);

  const resetAddForm = () => {
    setLabel('');
    setMetric('visits');
    setThreshold(0);
    setType('percent');
    setValue(0);
    setVisibleOnBill(true);
    setOutletId(null);
  };

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return alert('Please enter a name / label for this discount rule.');
    if (threshold <= 0) return alert('Threshold must be greater than 0');
    if (value <= 0) return alert('Discount value must be greater than 0');

    const token = getStoredToken();
    if (!token) return alert('Please sign in first.');

    try {
      const res = await fetch(`${BACKEND}/discount-rules/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          label: label.trim(),
          metric,
          threshold: parseFloat(String(threshold)),
          type,
          value: parseFloat(String(value)),
          visible_on_bill: Boolean(visibleOnBill),
          outlet_id: outletId ? Number(outletId) : null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Create failed (${res.status})`);
      }

      const saved = await res.json();
      onAddRule({
        id: String(saved.id),
        label: saved.label || undefined,
        metric: saved.metric,
        threshold: saved.threshold,
        type: saved.type,
        value: saved.value,
        visibleOnBill: saved.visible_on_bill,
        outletId: saved.outlet_id ?? null,
      } as any);

      resetAddForm();
    } catch (err: any) {
      console.error('Create discount rule error:', err);
      alert(err.message || 'Could not save discount rule.');
    }
  };

  const startEdit = (r: DiscountRule) => {
    setEditingId(String(r.id));
    setEditLabel(r.label || '');
    setEditMetric(r.metric);
    setEditThreshold(r.threshold);
    setEditType(r.type);
    setEditValue(r.value);
    setEditVisibleOnBill(r.visibleOnBill);
    setEditOutletId(r.outletId ?? null);
  };

  const saveEdit = async (id: string) => {
    if (!editLabel.trim()) return alert('Please enter a name / label for this discount rule.');
    if (editThreshold <= 0) return alert('Threshold must be greater than 0');
    if (editValue <= 0) return alert('Discount value must be greater than 0');

    const token = getStoredToken();
    if (!token) return alert('Please sign in first.');

    const ruleIdNum = parseInt(String(id), 10);
    if (isNaN(ruleIdNum)) return alert('Invalid Rule ID.');

    try {
      const res = await fetch(`${BACKEND}/discount-rules/${ruleIdNum}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          label: editLabel.trim(),
          metric: editMetric,
          threshold: parseFloat(String(editThreshold)),
          type: editType,
          value: parseFloat(String(editValue)),
          visible_on_bill: Boolean(editVisibleOnBill),
          outlet_id: editOutletId ? Number(editOutletId) : null,
        }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Update failed (${res.status})`);
      }

      const saved = await res.json();
      onUpdateRule({
        id: String(saved.id),
        label: saved.label || undefined,
        metric: saved.metric,
        threshold: saved.threshold,
        type: saved.type,
        value: saved.value,
        visibleOnBill: saved.visible_on_bill,
        outletId: saved.outlet_id ?? null,
        createdAt: saved.created_at || new Date().toISOString(),
      });

      setEditingId(null);
    } catch (err: any) {
      console.error('Update discount rule error:', err);
      alert(err.message || 'Could not update discount rule.');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this discount rule?')) return;
    const token = getStoredToken();
    if (!token) return;

    try {
      const res = await fetch(`${BACKEND}/discount-rules/${Number(id)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok && res.status !== 204 && res.status !== 404) {
        throw new Error(`Delete failed (${res.status})`);
      }

      onDeleteRule(String(id));
    } catch (err: any) {
      console.error('Delete discount rule error:', err);
      alert(err.message || 'Could not delete discount rule.');
    }
  };

  const sortedRules = [...rules].sort((a, b) => a.threshold - b.threshold);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        style={{ fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
      >

        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 shrink-0">
          <div>
            <h3 className="text-sm font-extrabold text-slate-800 flex items-center gap-2">
              <Tag className="w-4 h-4 text-emerald-600" />
              Discount Rules
            </h3>
            <p className="text-[10.5px] text-slate-450 mt-0.5">
              Rules apply automatically to every matching client, based on their visit count or total spend. Add as many as you like — the best matching rule wins.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700 cursor-pointer rounded-lg hover:bg-slate-50">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">

          {/* Add new rule form */}
          <form onSubmit={handleAdd} className="border border-slate-200 rounded-xl p-4 space-y-3 bg-slate-50/60">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" />
              Add New Rule
            </div>

            <div className="space-y-1">
              <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">
  Rule Name / Label <span className="text-red-500">*</span>
</label>
<input
  type="text"
  required
  placeholder="e.g. Loyal Customer, Gold Member"
  value={label}
  onChange={(e) => setLabel(e.target.value)}
  className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
/>
            </div>

            <div className="space-y-1">
              <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">Applies To</label>
              <select
                value={outletId === null ? '' : String(outletId)}
                onChange={(e) => setOutletId(e.target.value === '' ? null : Number(e.target.value))}
                className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
              >
                <option value="">All outlets (global)</option>
                {outlets.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">When</label>
                <select
                  value={metric}
                  onChange={(e) => setMetric(e.target.value as 'visits' | 'spend')}
                  className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
                >
                  <option value="visits">Visit count reaches</option>
                  <option value="spend">Total spend reaches</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">
                  {metric === 'visits' ? 'Min visits' : 'Min spend'}
                </label>
                <input
                  type="number"
                  min={0}
                  value={threshold}
                  onChange={(e) => setThreshold(parseFloat(e.target.value) || 0)}
                  className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">Discount Type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as 'percent' | 'fixed')}
                  className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
                >
                  <option value="percent">Percent off</option>
                  <option value="fixed">Fixed amount off</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">
                  {type === 'percent' ? 'Percent (%)' : 'Amount'}
                </label>
                <input
                  type="number"
                  min={0}
                  value={value}
                  onChange={(e) => setValue(parseFloat(e.target.value) || 0)}
                  className="w-full px-2.5 py-1.5 text-xs font-semibold text-slate-700 bg-white border border-slate-200 rounded focus:outline-none"
                />
              </div>
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={visibleOnBill}
                onChange={(e) => setVisibleOnBill(e.target.checked)}
                className="w-3.5 h-3.5"
              />
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Show as a line on the bill</span>
            </label>
            <p className="text-[9.5px] text-slate-400 leading-snug pl-5 -mt-2">
              {visibleOnBill ? 'Will appear as a visible discount line item.' : 'Silently folded into the total — no line shown on the printed bill.'}
            </p>

            <button
              type="submit"
              className="w-full py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-all cursor-pointer"
            >
              Add Rule
            </button>
          </form>

          {/* Existing rules list */}
          <div className="space-y-2">
            <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
              Active Rules ({rules.length})
            </div>

            {sortedRules.length === 0 && (
              <div className="text-center py-6 text-[11px] text-slate-400 border border-dashed border-slate-200 rounded-xl">
                No discount rules yet. Add one above to start rewarding repeat customers automatically.
              </div>
            )}

            {sortedRules.map((r) => {
              const isEditing = editingId === r.id;
              return (
                <div key={r.id} className="border border-slate-200 rounded-lg p-3 bg-white">
                  {isEditing ? (
                    <div className="space-y-2.5">
                      <label className="text-[9px] font-bold text-slate-400 uppercase font-mono">
                        Rule Name / Label <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        required
                        placeholder="e.g. Loyal Customer, Gold Member"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                      />
                      <select
                        value={editOutletId === null ? '' : String(editOutletId)}
                        onChange={(e) => setEditOutletId(e.target.value === '' ? null : Number(e.target.value))}
                        className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                      >
                        <option value="">All outlets (global)</option>
                        {outlets.map(o => (
                          <option key={o.id} value={o.id}>{o.name}</option>
                        ))}
                      </select>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={editMetric}
                          onChange={(e) => setEditMetric(e.target.value as 'visits' | 'spend')}
                          className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                        >
                          <option value="visits">Visit count reaches</option>
                          <option value="spend">Total spend reaches</option>
                        </select>
                        <input
                          type="number"
                          min={0}
                          value={editThreshold}
                          onChange={(e) => setEditThreshold(parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <select
                          value={editType}
                          onChange={(e) => setEditType(e.target.value as 'percent' | 'fixed')}
                          className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                        >
                          <option value="percent">Percent off</option>
                          <option value="fixed">Fixed amount off</option>
                        </select>
                        <input
                          type="number"
                          min={0}
                          value={editValue}
                          onChange={(e) => setEditValue(parseFloat(e.target.value) || 0)}
                          className="w-full px-2 py-1 text-xs font-semibold text-slate-700 bg-slate-50 border border-slate-200 rounded focus:outline-none"
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editVisibleOnBill}
                          onChange={(e) => setEditVisibleOnBill(e.target.checked)}
                          className="w-3.5 h-3.5"
                        />
                        <span className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">Show as a line on the bill</span>
                      </label>
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => saveEdit(r.id)}
                          className="flex-1 py-1.5 bg-purple-700 hover:bg-blue-700 text-white rounded text-[11px] font-bold flex items-center justify-center gap-1 cursor-pointer"
                        >
                          <Save className="w-3.5 h-3.5" />
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="py-1.5 px-3 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded text-[11px] font-bold cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-[11px] font-semibold text-slate-700">
                        {describeDiscountRule(r)}
                        <span className="block text-[9.5px] font-bold text-slate-400 mt-0.5">
                          {r.outletId ? (outlets.find(o => o.id === r.outletId)?.name || `Outlet #${r.outletId}`) : 'All outlets'}
                        </span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => startEdit(r)}
                          className="p-1.5 border border-slate-150 hover:bg-slate-50 rounded text-slate-500 hover:text-slate-800 cursor-pointer"
                          title="Edit rule"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(r.id)}
                          className="p-1.5 border border-slate-150 hover:bg-red-50 hover:border-red-200 text-slate-500 hover:text-red-600 cursor-pointer"
                          title="Delete rule"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-100 px-6 py-3 shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold cursor-pointer"
          >
            Done
          </button>
        </div>

      </div>
    </div>
  );
}
